import React, { useMemo, useState } from 'react';
import { Select, Tooltip } from 'antd';
import { MenuItemText } from '../../../../../../../shared/globalStyling/styledText';
import {
  formatNumber,
  formatNumber2SigFig,
} from '../../../../../../../../core/utils/campaigns';
import CampaignsDonut from './CampaignsDonut';
import { randomColors } from '../../../../../../../../core/utils/colors';
import DataTable from '../../../../../../../shared/designSystem/components/DataTable';

const { Option } = Select;

const SHOW_BY_OPTIONS = [
  { value: 'impressions', text: 'Ad Impressions' },
  { value: 'clicks', text: 'Clicks' },
];

// =============================================================================
// CampaignsCard — aggregate campaign breakdown shown on the Display tab when
// multiple DSPs are wired up to one campaign order (trafficker-only view).
//
// Structural twin of LineItemsCard without Status / Value Add / Action columns
// — simpler 5-column table + breakdown donut. Uses the shared DataTable and
// BreakdownRingChart primitives.
// =============================================================================

const numericSort = (key) => (rowA, rowB) => {
  return (Number(rowA.original[key]) || 0) - (Number(rowB.original[key]) || 0);
};

const nameSort = (rowA, rowB) => {
  const a = rowA.original.name || '';
  const b = rowB.original.name || '';
  return String(a).localeCompare(String(b));
};

const CampaignsCard = ({ data }) => {
  const [campaignsDimension, setCampaignsDimension] = useState('impressions');

  // Filter out rows without a name (parity with v1's useEffect filter) and
  // attach a stable random color per row. useMemo keyed on data identity so
  // colors don't reshuffle on every parent re-render.
  const coloredData = useMemo(() => {
    if (!data || data.length === 0) return [];
    return data
      .filter((item) => item.name)
      .map((d) => ({
        ...d,
        value: d.name,
        color: randomColors(),
      }));
  }, [data]);

  const columns = [
    {
      id: 'name',
      accessorKey: 'name',
      header: 'Name',
      sortable: true,
      sortingFn: nameSort,
      meta: { align: 'left', width: 260 },
      cell: ({ getValue }) => {
        const name = getValue();
        return (
          <Tooltip overlayClassName="mjl-ds" title={name}>
            <span className="campaign-overview__creatives-name">{name}</span>
          </Tooltip>
        );
      },
    },
    {
      id: 'flightDates',
      accessorFn: (row) => `${row.startDate} - ${row.endDate}`,
      header: 'Flight Dates',
      sortable: false,
      meta: { align: 'left', width: 200 },
      cell: ({ row }) => (
        <span className="campaign-overview__lineitem-flight">
          {row.original.startDate} &rarr; {row.original.endDate}
        </span>
      ),
    },
    {
      id: 'impressions',
      accessorKey: 'impressions',
      header: 'Impressions',
      sortable: true,
      sortingFn: numericSort('impressions'),
      meta: { align: 'right', width: 130 },
      cell: ({ getValue }) => formatNumber(getValue()),
    },
    {
      id: 'clicks',
      accessorKey: 'clicks',
      header: 'Clicks',
      sortable: true,
      sortingFn: numericSort('clicks'),
      meta: { align: 'right', width: 110 },
      cell: ({ getValue }) => formatNumber(getValue()),
    },
    {
      id: 'ctr',
      accessorKey: 'ctr',
      header: 'CTR',
      sortable: true,
      sortingFn: numericSort('ctr'),
      meta: { align: 'right', width: 90 },
      cell: ({ getValue }) => `${formatNumber2SigFig(getValue())}%`,
    },
  ];

  const totalImpressions = coloredData.reduce(
    (sum, d) => sum + (parseFloat(d.impressions) || 0),
    0
  );
  const totalClicks = coloredData.reduce(
    (sum, d) => sum + (parseFloat(d.clicks) || 0),
    0
  );

  const renderMobileCard = (record) => (
    <>
      <div className="campaign-overview__creatives-card-head">
        <div className="campaign-overview__creatives-card-name">
          <span>{record.name}</span>
        </div>
      </div>
      <div className="campaign-overview__lineitem-card-meta">
        <span className="campaign-overview__lineitem-flight">
          {record.startDate} &rarr; {record.endDate}
        </span>
      </div>
      <div className="campaign-overview__creatives-card-metrics">
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">Impressions</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {formatNumber(record.impressions)}
          </span>
        </div>
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">Clicks</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {formatNumber(record.clicks)}
          </span>
        </div>
        <div className="campaign-overview__creatives-card-metric">
          <span className="campaign-overview__creatives-card-metric-label">CTR</span>
          <span className="campaign-overview__creatives-card-metric-value">
            {formatNumber2SigFig(record.ctr)}%
          </span>
        </div>
      </div>
    </>
  );

  return (
    <section className="campaign-overview__creatives">
      <header className="campaign-overview__creatives-head">
        <div className="campaign-overview__creatives-eyebrow mono-eyebrow">
          Campaigns
        </div>
      </header>

      <div className="campaign-overview__creatives-body">
        <div className="campaign-overview__creatives-table">
          <DataTable
            data={coloredData}
            columns={columns}
            pageSize={10}
            mobileBreakpoint="md"
            renderMobileCard={renderMobileCard}
            summaryRow={{
              labelColumnId: 'name',
              label: 'Totals + Averages',
              cells: {
                impressions: formatNumber(totalImpressions),
                clicks: formatNumber(totalClicks),
                ctr: '-',
              },
            }}
            emptyState="No campaigns yet."
          />
        </div>

        <aside className="campaign-overview__creatives-breakdown">
          <div className="campaign-overview__creatives-breakdown-head">
            <div className="campaign-overview__creatives-breakdown-eyebrow mono-eyebrow">
              Breakdown
            </div>
            <Select
              value={campaignsDimension}
              onChange={(v) => setCampaignsDimension(v)}
              getPopupContainer={(trigger) => trigger.parentNode}
              popupClassName="mjl-ds"
              className="campaign-overview__creatives-showby"
              size="small"
            >
              {SHOW_BY_OPTIONS.map((option) => (
                <Option key={option.value} value={option.value}>
                  <MenuItemText text={option.text} />
                </Option>
              ))}
            </Select>
          </div>
          <CampaignsDonut
            data={coloredData}
            height={480}
            selectedDimension={campaignsDimension}
          />
        </aside>
      </div>
    </section>
  );
};

export default CampaignsCard;
